import type { NavGroup, NavLinkItem, NavCapabilityGate } from "../../nav/navConfig.js";
import { hasPermission } from "../permissions.js";

/** The account shape `hasPermission` accepts, without coupling to its name. */
type GateUser = Parameters<typeof hasPermission>[0];

/**
 * The context a nav tree is filtered against. Plain data plus two resolved
 * predicates, so a group's visibility is a pure function of its inputs: the
 * hook builds this once per render and the same decision powers the sidebar,
 * the command palette and the tests.
 */
export interface NavVisibilityContext {
  isAdmin: boolean;
  user: GateUser;
  installedApps: ReadonlySet<string>;
  /**
   * A SECOND presence source, unioned with `installedApps` for the
   * `requiresAnyApp` gate: tokens a provider reports as present box-wide even
   * when the viewer's own package set does not list them (e.g. a Media Requests
   * family `seerr`/`ombi` reachable through a remote-configured key, or installed
   * under another user). Empty when the caller cannot or did not resolve it —
   * the local-install path then governs on its own.
   */
  configuredRequestProviders: ReadonlySet<string>;
  /** System feature flags resolved to a single yes/no (e.g. `wsdashboard`). */
  isFeatureVisible: (feature?: string) => boolean;
  /** Async capability gates (AMC, runtime presence) resolved to a yes/no. */
  passesCapability: (gate: NavCapabilityGate | undefined) => boolean;
}

/** The resolved system flags and grants a nav `requiresFeature` gate reads. */
export interface NavFeatureState {
  streamingEnabled: boolean;
  webconsoleEnabled: boolean;
  transferEnabled: boolean;
  deleteProtectionEnabled: boolean;
  requestsGranted: boolean;
  /** A media server is present: installed by QuickBox or connected by URL. */
  hasMediaServer: boolean;
}

/**
 * Resolves a `requiresFeature` token to a yes/no. WSDashboard follows its
 * opt-in flag alone: a media server QuickBox did not install is connected from
 * the dashboard's own Connection Settings, so an install check here would hide
 * the very entry that leads there. Media Requests is a grant, never inferred
 * from what happens to be installed, and it also needs a media server to act on.
 * `wsdashboard-media-server` is for entries that only make sense with a server
 * present (the TV Guide): the flag AND a present server.
 */
export const isNavFeatureVisible = (feature: string | undefined, state: NavFeatureState): boolean => {
  if (!feature) return true;
  if (feature === "wsdashboard") return state.streamingEnabled;
  if (feature === "wsdashboard-media-server") return state.streamingEnabled && state.hasMediaServer;
  if (feature === "webconsole") return state.webconsoleEnabled;
  if (feature === "inter-instance-transfer") return state.transferEnabled;
  if (feature === "delete-protection") return state.deleteProtectionEnabled;
  if (feature === "media-requests") return state.requestsGranted && state.hasMediaServer;
  return true;
};

/** A nav entry survives when every gate it declares is satisfied. */
export const filterNavItem = (item: NavLinkItem, ctx: NavVisibilityContext): boolean => {
  if (item.requiresRole === "admin" && !ctx.isAdmin) return false;
  if (!ctx.isFeatureVisible(item.requiresFeature)) return false;
  if (item.requiresPermission && !hasPermission(ctx.user, item.requiresPermission)) return false;
  if (item.requiresApp && !ctx.installedApps.has(item.requiresApp)) return false;
  if (
    item.requiresAnyApp &&
    !item.requiresAnyApp.some((app) => ctx.installedApps.has(app) || ctx.configuredRequestProviders.has(app))
  )
    return false;
  if (!ctx.passesCapability(item.requiresCapability)) return false;
  return true;
};

/**
 * Filter a nav group and its subtree.
 *
 * A feature flag on a group is AUTHORITATIVE: when the system feature is off
 * the whole subtree is hidden regardless of any individually-grantable child,
 * so a child can never resurrect a feature-gated parent. Role and permission
 * gates stay non-authoritative below — the children decide whether the group
 * appears, so a grantee sees exactly the one entry they were given inside an
 * otherwise admin-only group, in its natural home, with no admin sibling.
 * (A landing page a granted user may not open is still withheld from the
 * header via `mayOpenGroupLanding`.)
 */
export const filterNavGroup = (group: NavGroup, ctx: NavVisibilityContext): NavGroup | null => {
  if (!ctx.isFeatureVisible(group.requiresFeature)) return null;

  const groupItems = group.items.filter((item) => filterNavItem(item, ctx));
  const childGroups = group.groups
    ?.map((child) => filterNavGroup(child, ctx))
    .filter((child): child is NavGroup => child !== null);
  if (!groupItems.length && (!childGroups || !childGroups.length)) return null;

  const mayOpenGroupLanding =
    !(group.requiresRole === "admin" && !ctx.isAdmin) &&
    ctx.isFeatureVisible(group.requiresFeature) &&
    !(group.requiresPermission && !hasPermission(ctx.user, group.requiresPermission)) &&
    ctx.passesCapability(group.requiresCapability);

  const { path: groupPath, ...rest } = group;
  return {
    ...rest,
    ...(mayOpenGroupLanding && groupPath ? { path: groupPath } : {}),
    items: groupItems,
    groups: childGroups?.length ? childGroups : undefined
  };
};
